<template>
  <WebsitePopupComponent v-model="isActive">
    <template v-if="isSendOtpStepActive">
      <p class="mb-3 text-primary">
        Click to choose one of the options and enter the required data to receive your one time password
      </p>
      <div class="ow-channel-selection-inputs mb-3">
        <div class="ow-channel-selection-input-wrapper">
          <label for="email-radio" class="ow-channel-selection-label" :class="{ 'is-disabled': isLoaderActive }">
            <div class="ow-channel-selection-label-icon email" :class="{ 'is-active': currentOtpChannel === 'email' }"></div>
            <span class="ow-channel-selection-label-text">Email</span>
          </label>
          <input
            id="email-radio"
            v-model="currentOtpChannel"
            value="email"
            type="radio"
            class="ow-channel-selection-input"
            :disabled="isLoaderActive"
            >
        </div>
        <div class="ow-channel-selection-input-wrapper">
          <label for="phone-radio" class="ow-channel-selection-label" :class="{ 'is-disabled': isLoaderActive }">
            <div class="ow-channel-selection-label-icon phone" :class="{ 'is-active': currentOtpChannel === 'phone' }"></div>
            <span class="ow-channel-selection-label-text">SMS</span>
          </label>
          <input
            id="phone-radio"
            v-model="currentOtpChannel"
            value="phone"
            type="radio"
            class="ow-channel-selection-input"
            :disabled="isLoaderActive"
            >
        </div>
        <!-- <div class="ow-channel-selection-input-wrapper is-disabled">
          <label for="whatsapp-radio" class="ow-channel-selection-label" :class="{ 'is-disabled': isLoaderActive }">
            <div class="ow-channel-selection-label-icon whatsapp" :class="{ 'is-active': currentOtpChannel === 'whatsapp' }"></div>
            <span class="ow-channel-selection-label-text">WhatsApp</span>
          </label>
          <input
            id="whatsapp-radio"
            v-model="currentOtpChannel"
            value="whatsapp"
            type="radio"
            disabled
            class="ow-channel-selection-input"
            >
            :disabled="isLoaderActive"
      </div> -->
      </div>
      <div class="ow-otp-contact-input-wrapper mb-3">
        <InputText
          v-if="contactInput.type === 'email'"
          v-model="contactInputEmailValue"
          is-required
          name="email"
          custom-id="email"
          type="email"
          :is-disabled="isLoaderActive"
          :widget-class="`${!isContactInputValid ? 'is-required': ''}`"
          :placeholder="contactInput.placeholder"
          />
        <InputPhone
          v-if="contactInput.type === 'phone'"
          v-model="contactInputPhoneValue"
          is-required
          default-country-code="+27"
          relative-container=".ow-popup-body"
          :top-country-codes="['+27', '+264']"
          :widget-class="`${!isContactInputValid ? 'is-required' : ''}`"
          :placeholder="contactInput.placeholder"
          />
      </div>
      <div class="ow-otp-contact-submit-wrapper">
        <button class="ow-button submit-button"
          :disabled="isLoaderActive || !isContactInputValid"
          @click="sendButtonClicked"
          >
          <span v-if="!isLoaderActive">Send</span>
          <OLoader
            v-model="isLoaderActive"
            class="button-default"
            name="btn-dots"
            />
        </button>
      </div>
    </template>
    <template v-else>
      <div class="ow-otp-sent-message mb-3">
        <div>A message with verification code has been sent to {{ currentOtpChannel === 'email' ? 'your email' : 'you by SMS' }}.</div>
        <div>Please enter the code to continue.</div>
      </div>
      <OtpValidationInputs ref="validationInputsRef"
        :length="6"
        :is-otp-valid="isOtpValid"
        @otp-completed="verifyPatientOtp"
        />
      <div v-if="isOtpValid === false && !isTokenExpired" class="ow-otp-invalid-message">
        The OTP you typed in was not correct. Try again or resend.
      </div>
      <div v-else-if="isTokenExpired" class="ow-otp-invalid-message is-expired">
        The OTP you typed in has expired.
      </div>
      <div class="ow-otp-resend-button-wrapper">
        <button class="ow-otp-resend-button" @click="resendOtp">
          Resend OTP
        </button>
        <div class="text-center">
          or
        </div>
        <button class="ow-otp-resend-button" @click="backToChannelSelection">
          Try another way
        </button>
      </div>
      <OLoader v-model="isResendLoaderActive" name="dots" container=".os-modal-body" />
    </template>
  </WebsitePopupComponent>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { useNuxtApp } from '#app';

import OtpValidationInputs from './OtpValidationInputs.vue';

const { $useToast } = useNuxtApp();

interface IResponse {
  success: boolean
  message?: string
  isExpired?: boolean
}

const emit = defineEmits(['otpSentToPatient', 'patientVerified']);

const isActive = defineModel({ default: false, type: Boolean });
const validationInputsRef = useTemplateRef('validationInputsRef');
const isSendOtpStepActive = ref(true);
const isLoaderActive = ref(false);
const isResendLoaderActive = ref(false);
const currentOtpChannel = ref('email');
const contactInputEmailValue = ref('');
const isOtpValid = ref(true);
const isTokenExpired = ref(false);

const contactInputPhoneValue = ref({
  countryCode: '+27',
  number: ''
});

const contactInput = computed(() => {
  return {
    type: currentOtpChannel.value === 'email' ? 'email' : 'phone',
    placeholder: currentOtpChannel.value === 'email' ? 'Please enter your email' : 'Please enter your cellphone number'
  };
});

const isContactInputValid = computed(() => {
  let isValid = false;

  if ((contactInputPhoneValue.value && contactInputPhoneValue.value.number !== '') || contactInputEmailValue.value !== '') {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const phoneRegex = /^(\+|\d)[0-9]{7,16}$/;

    if (currentOtpChannel.value === 'email') isValid = emailRegex.test(contactInputEmailValue.value);
    else if (currentOtpChannel.value === 'phone' || currentOtpChannel.value === 'whatsapp') {
      const digitsOnly = contactInputPhoneValue.value.number.replace(/[\s-]/g, '');
      isValid = phoneRegex.test(contactInputPhoneValue.value.number) && digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }
  }

  return isValid;
});

const resolveContactInfo = (channel: string) => {
  if (channel === 'email') return contactInputEmailValue.value;
  return contactInputPhoneValue.value.number;
};

const resetContactFields = () => {
  contactInputPhoneValue.value = { countryCode: '+27', number: '' };
  contactInputEmailValue.value = '';
};

const backToChannelSelection = () => {
  // document.cookie = '';
  currentOtpChannel.value = 'email';
  isSendOtpStepActive.value = true;
  resetContactFields();
};

const generateAndSendPatientOtp = async () => {
  try {
    const deviceId = window.crypto.randomUUID();
    document.cookie = `deviceId=${deviceId}`;

    const otpToken = await $fetch('/api/auth/generate-otp', {
      method: 'post',
      body: {
        channel: currentOtpChannel.value,
        contactInfo: resolveContactInfo(currentOtpChannel.value),
        deviceId: deviceId
      }
    }) as IResponse;

    if (!otpToken) throw new Error('There was a problem generating OTP');
    document.cookie = `userToken=${otpToken}`;
    document.cookie = `userContact=${resolveContactInfo(currentOtpChannel.value)}`;
    document.cookie = `channel=${currentOtpChannel.value}`;
  }
  catch (error) {
    console.log(error);
  }
};

const resendOtp = async () => {
  isResendLoaderActive.value = true;
  await generateAndSendPatientOtp();
  isOtpValid.value = true;
  isTokenExpired.value = false;
  validationInputsRef.value?.resetFields();
  $useToast().show({ message: 'The OTP has been resent!', duration: 5000, container: '.os-modal' });
  isResendLoaderActive.value = false;
};

const activateOtpVerification = async () => {
  try {
    isSendOtpStepActive.value = false;
    setTimeout(() => {
      validationInputsRef.value?.focusFirstInput();
    }, 300);
  }
  catch (error) {
    console.log(error);
  }
};

const sendButtonClicked = async () => {
  // TODO - do this on enter press
  isLoaderActive.value = true;
  await generateAndSendPatientOtp();
  isLoaderActive.value = false;
  emit('otpSentToPatient');
  activateOtpVerification();
};

const verifyPatientOtp = async (otpValue: string) => {
  // Resolving the values we stored in the cookie to use them in the otp verification
  const cookieValues = document.cookie.split(';');
  const deviceId = cookieValues.find(value => value.includes('deviceId'))?.split('deviceId=')[1];
  const otpToken = cookieValues.find(value => value.includes('userToken'))?.split('userToken=')[1];
  const userContact = cookieValues.find(value => value.includes('userContact'))?.split('userContact=')[1];
  const channel = cookieValues.find(value => value.includes('channel'))?.split('channel=')[1];
  try {
    const response = await $fetch('/api/auth/verify-otp-token', {
      method: 'post',
      body: {
        deviceId,
        otpToken,
        otpValue,
        userContact,
        channel,
        otpSubmitTime: new Date()
      }
    }) as IResponse;
    if (response.success) {
      isActive.value = false;
      isTokenExpired.value = false;
      emit('patientVerified', true);
    }
    else {
      isOtpValid.value = false;
      if (response.isExpired) {
        isTokenExpired.value = true;
      }
    }
  }
  catch (error) {
    console.error('error verify Otp:', error);
  }
};

watch(() => currentOtpChannel.value, (newVal) => {
  if (newVal) {
    resetContactFields();
  }
});
</script>

<style lang='scss'>
.ow-channel-selection-inputs {
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: center;
  gap: 30px;
  & .ow-channel-selection-input-wrapper {
    &.is-disabled {
      opacity: 0.5;
      user-select: none;
      cursor: not-allowed;
      & .ow-channel-selection-label {
        pointer-events: none;
      }
    }
    & .ow-channel-selection-label {
      flex: 0 0 fit-content;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      &.is-disabled {
        pointer-events: none;
        opacity: 0.65;
      }
      & .ow-channel-selection-label-icon {
        width: 70px;
        height: 70px;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center center;
        transition: all ease 0.25s;
        &:hover,
        &.is-active {
          cursor: pointer;
          &.email {
            background-image: url('/images/EmailIconFilled.png');
          }
          &.phone {
            background-image: url('/images/PhoneIconFilled.png');
          }
          &.whatsapp {
            background-image: url('/images/WhatsAppFilled.png');
          }
        }
        &.email {
          background-image: url('/images/EmailIconOutlined.png');
        }
        &.phone {
          background-image: url('/images/PhoneIconOutlined.png');
        }
        &.whatsapp {
          background-image: url('/images/WhatsAppOutlined.png');
        }
      }
      & .ow-channel-selection-label-text {
        color: #0e365d;
        font-weight: 500;
      }
    }
    & .ow-channel-selection-input {
      display: none;
    }
  }
}
.ow-otp-contact-input-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 100%;
  & .os-input-widget {
    flex: 1 1 50%;
    max-width: 400px;
  }
}
.ow-otp-contact-submit-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  & .ow-button {
    height: 40px !important;
  }
}

.ow-otp-sent-message {
  white-space: pre-wrap;
  text-align: center;
  color: #0e365d;
}

.ow-otp-invalid-message {
  color: #dc3545;
  text-align: center;
  margin-bottom: 10px;
}

.ow-otp-resend-button-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;

  font-size: 0.9rem;
  justify-content: center;
  & .ow-otp-resend-button {
    border: none;
    padding: 0;
    color: #0e365d;
    font-weight: 500;
    text-decoration: underline;
    cursor: pointer;
    background-color: unset;
    margin: 0 auto;
    height: 1rem;
  }
}
</style>
